import React, { useState, useMemo } from "react";
import { Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { glossaryTerms, categories, alphabet } from "../../data/glossaryTerms";
import { useProductTerm } from "../../hooks/useProductTerm";
import { foldSearch } from "../../utils/searchFold";
import GlossaryExampleModal from "./GlossaryExampleModal";

const CARD_WIDTH_BY_SIZE = {
  sm: "w-[278px]",
  md: "w-[308px]",
};

const GlossaryCard = ({ term, index, size, replaceTerm, onOpenExample }) => {
  const hasExample = Boolean(term.exampleImage);
  const handleClick = hasExample ? () => onOpenExample(term) : undefined;
  const handleKeyDown = hasExample
    ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenExample(term);
        }
      }
    : undefined;
  const widthClass = CARD_WIDTH_BY_SIZE[size] ?? CARD_WIDTH_BY_SIZE.md;

  return (
    <div
      role={hasExample ? "button" : undefined}
      tabIndex={hasExample ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`bg-white border border-[#F5F5F5] rounded-[24px] p-6 ${widthClass} flex flex-col gap-3 animate-fadeIn ${
        hasExample
          ? "cursor-pointer transition-shadow hover:shadow-md hover:border-[#E0E0E0] focus:outline-none focus:ring-2 focus:ring-[#0E49B5]"
          : ""
      }`}
      style={{
        animationDelay: `${index * 50}ms`,
        animationFillMode: "forwards",
        opacity: 0,
      }}
    >
      <h3
        className="text-base font-medium leading-5 tracking-[-0.16px] text-[rgba(0,0,0,0.85)]"
        style={{ fontFamily: "Satoshi, sans-serif" }}
      >
        {replaceTerm(term.term)}
      </h3>
      <p
        className="text-sm font-normal leading-[22px] text-[rgba(0,0,0,0.55)]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {replaceTerm(term.definition)}
      </p>
      {hasExample && (
        <span
          className="text-xs font-medium text-[#0E49B5] mt-1"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          See example →
        </span>
      )}
    </div>
  );
};

// Glossary tab body for LearnHubPage. Renders search/tabs/cards but no outer
// page layout or H1, which the hub owns.
const GlossaryContent = () => {
  const [viewMode, setViewMode] = useState("category");
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeLetter, setActiveLetter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [exampleTerm, setExampleTerm] = useState(null);
  const { replaceTerm } = useProductTerm();

  const availableLetters = useMemo(() => {
    const letters = new Set();
    glossaryTerms.forEach((term) => {
      letters.add(term.term[0].toUpperCase());
    });
    return Array.from(letters).sort();
  }, []);

  const groupedByCategory = useMemo(() => {
    const groups = {};
    const validCategories = categories.filter((cat) => cat !== "All Categories");

    validCategories.forEach((category) => {
      let categoryTerms = glossaryTerms.filter((term) => term.category === category);

      if (searchQuery.trim()) {
        const query = foldSearch(searchQuery);
        categoryTerms = categoryTerms.filter(
          (term) =>
            foldSearch(replaceTerm(term.term)).includes(query) ||
            foldSearch(replaceTerm(term.definition)).includes(query)
        );
      }

      categoryTerms.sort((a, b) => a.term.localeCompare(b.term));

      if (categoryTerms.length > 0) {
        groups[category] = categoryTerms;
      }
    });

    return groups;
  }, [searchQuery, replaceTerm]);

  const groupedByLetter = useMemo(() => {
    const groups = {};

    alphabet.forEach((letter) => {
      const letterTerms = glossaryTerms.filter(
        (term) => term.term[0].toUpperCase() === letter
      );

      if (searchQuery.trim()) {
        const query = foldSearch(searchQuery);
        groups[letter] = letterTerms.filter(
          (term) =>
            foldSearch(replaceTerm(term.term)).includes(query) ||
            foldSearch(replaceTerm(term.definition)).includes(query)
        );
      } else {
        groups[letter] = letterTerms;
      }
    });

    return groups;
  }, [searchQuery, replaceTerm]);

  const categoriesToDisplay = useMemo(() => {
    if (activeCategory) {
      return [activeCategory];
    }
    return Object.keys(groupedByCategory);
  }, [activeCategory, groupedByCategory]);

  return (
    <div className="flex flex-col gap-6">
      {/* Search and Tabs Bar */}
      <div className="flex gap-6 items-center">
        <div className="inline-flex rounded-full border border-[rgba(0,0,0,0.15)] p-1 bg-white shrink-0">
          <button
            onClick={() => {
              setViewMode("category");
              setActiveLetter(null);
            }}
            className={`px-4 py-1.5 rounded-full text-sm font-normal transition-colors ${
              viewMode === "category"
                ? "bg-[#F5F8FC] text-[#0E49B5] border border-[#0E49B5]"
                : "text-[rgba(0,0,0,0.45)]"
            }`}
          >
            Category
          </button>
          <button
            onClick={() => {
              setViewMode("alphabetic");
              setActiveCategory(null);
            }}
            className={`px-4 py-1.5 rounded-full text-sm font-normal transition-colors ${
              viewMode === "alphabetic"
                ? "bg-[#F5F8FC] text-[#0E49B5] border border-[#0E49B5]"
                : "text-[rgba(0,0,0,0.45)]"
            }`}
          >
            Alphabetic
          </button>
        </div>

        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search glossary terms"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            prefix={
              <SearchOutlined
                style={{ color: "rgba(0,0,0,0.45)", marginRight: 8 }}
              />
            }
            style={{
              borderRadius: "999px",
              border: "1px solid rgba(0,0,0,0.15)",
              padding: "8px 30px 8px 22px",
              fontSize: "16px",
            }}
          />
        </div>
      </div>

      <div className="flex gap-4 items-start">
        {viewMode === "category" ? (
          <div className="flex flex-col gap-6 py-4 shrink-0 w-[153px]">
            <button
              onClick={() => setActiveCategory(null)}
              className={`text-left text-sm font-medium leading-[22px] transition-colors ${
                activeCategory === null
                  ? "text-[#0E49B5]"
                  : "text-[rgba(0,0,0,0.45)] hover:text-[rgba(0,0,0,0.85)]"
              }`}
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              All
            </button>
            {categories
              .filter((cat) => cat !== "All Categories")
              .map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`text-left text-sm font-medium leading-[22px] transition-colors ${
                    activeCategory === category
                      ? "text-[#0E49B5]"
                      : "text-[rgba(0,0,0,0.45)] hover:text-[rgba(0,0,0,0.85)]"
                  }`}
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {category}
                </button>
              ))}
          </div>
        ) : (
          <div className="flex flex-col gap-1 py-0.5 px-3 shrink-0">
            {alphabet.map((letter) => {
              const isAvailable = availableLetters.includes(letter);
              const isActive = activeLetter === letter;

              return (
                <button
                  key={letter}
                  onClick={() => {
                    if (isAvailable) {
                      setActiveLetter(isActive ? null : letter);
                      if (!isActive) {
                        setTimeout(() => {
                          const element = document.getElementById(`letter-${letter}`);
                          if (element) {
                            element.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }, 100);
                      }
                    }
                  }}
                  disabled={!isAvailable}
                  className={`text-sm font-medium leading-[22px] text-center py-0.5 transition-colors ${
                    isActive
                      ? "text-[#0E49B5]"
                      : isAvailable
                      ? "text-[rgba(0,0,0,0.45)] hover:text-[rgba(0,0,0,0.85)]"
                      : "text-[rgba(0,0,0,0.25)] cursor-not-allowed"
                  }`}
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-1 flex flex-col gap-9">
          {viewMode === "category" ? (
            <div className="flex flex-col gap-9">
              {categoriesToDisplay.length > 0 ? (
                categoriesToDisplay.map((category) => {
                  const terms = groupedByCategory[category];
                  if (!terms || terms.length === 0) return null;

                  return (
                    <div key={category} className="flex flex-col gap-5">
                      <div className="px-4">
                        <h2
                          className="text-2xl font-medium leading-7 tracking-[-0.24px] text-[#0E49B5]"
                          style={{ fontFamily: "Satoshi, sans-serif" }}
                        >
                          {category}
                        </h2>
                      </div>
                      <div className="flex flex-wrap gap-6">
                        {terms.map((term, index) => (
                          <GlossaryCard
                            key={term.term}
                            term={term}
                            index={index}
                            size="sm"
                            replaceTerm={replaceTerm}
                            onOpenExample={setExampleTerm}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-16">
                  <p className="text-[rgba(0,0,0,0.45)] text-base">
                    No terms found for the selected filters.
                  </p>
                  <button
                    onClick={() => {
                      setActiveCategory(null);
                      setSearchQuery("");
                    }}
                    className="mt-4 text-[#0E49B5] hover:underline font-medium"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-9">
              {activeLetter ? (
                groupedByLetter[activeLetter] && groupedByLetter[activeLetter].length > 0 ? (
                  <div className="flex flex-col gap-5" id={`letter-${activeLetter}`}>
                    <div className="px-4">
                      <h2
                        className="text-2xl font-medium leading-7 tracking-[-0.24px] text-[#0E49B5]"
                        style={{ fontFamily: "Satoshi, sans-serif" }}
                      >
                        {activeLetter}
                      </h2>
                    </div>
                    <div className="flex flex-wrap gap-6">
                      {groupedByLetter[activeLetter].map((term, index) => (
                        <GlossaryCard
                          key={term.term}
                          term={term}
                          index={index}
                          size="md"
                          replaceTerm={replaceTerm}
                          onOpenExample={setExampleTerm}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <p className="text-[rgba(0,0,0,0.45)] text-base">
                      No terms found for letter {activeLetter}.
                    </p>
                  </div>
                )
              ) : (
                alphabet.map((letter) => {
                  const terms = groupedByLetter[letter];
                  if (!terms || terms.length === 0) return null;

                  return (
                    <div key={letter} className="flex flex-col gap-5" id={`letter-${letter}`}>
                      <div className="px-4">
                        <h2
                          className="text-2xl font-medium leading-7 tracking-[-0.24px] text-[#0E49B5]"
                          style={{ fontFamily: "Satoshi, sans-serif" }}
                        >
                          {letter}
                        </h2>
                      </div>
                      <div className="flex flex-wrap gap-6">
                        {terms.map((term, index) => (
                          <GlossaryCard
                            key={term.term}
                            term={term}
                            index={index}
                            size="md"
                            replaceTerm={replaceTerm}
                            onOpenExample={setExampleTerm}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
      <GlossaryExampleModal
        open={Boolean(exampleTerm)}
        onClose={() => setExampleTerm(null)}
        term={exampleTerm ? replaceTerm(exampleTerm.term) : ""}
        definition={exampleTerm ? replaceTerm(exampleTerm.definition) : ""}
        imageFileName={exampleTerm?.exampleImage}
      />
    </div>
  );
};

export default GlossaryContent;
